import { Badge, Box, BoxProps, Button, Flex, Heading, HStack, Image, Text } from '@chakra-ui/react';
import { checkedListSx, checkMark } from '@core/components/List/Checked/CheckedList';
import Markdown from '@core/components/Markdown/Markdown';
import { isFreeTrialPlan, isFreeType, isTrialPlan } from '@waitroom/common';
import { CMS, UserSubscriptionPlan } from '@waitroom/models';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { badgeSx, headingSx, priceSx, subtextSx, wrapperSx } from './Product.styles';
export interface ProductProps extends BoxProps {
  data: CMS.PricingPlan;
  annual?: boolean;
  currentPlan?: UserSubscriptionPlan;
  isTrial?: boolean;
}

const ns = 'pricing.table.';

export const CmsProduct = ({ data, currentPlan, ...rest }: ProductProps): ReactElement | null => {
  const { t } = useTranslation();
  const type = data.type ?? 'free';
  const typeNs = `${ns}${type}.`;
  const isBest = data.isBest;
  const isFree = type === 'free';
  const features = data.features;
  const currentType = currentPlan?.planConfig?.paddleProductName;
  const currentTrial = isTrialPlan(currentPlan);

  if (!type) return null;
  return (
    <Box
      sx={wrapperSx}
      border={isBest ? '3px solid' : ''}
      bgColor={data.backgroundColor ?? (isBest ? undefined : 'red.50')}
      {...rest}
    >
      {isBest && <Badge sx={badgeSx}>{t(`${ns}bestBadge`)}</Badge>}
      <Box minH={6} mb={2} />
      <Heading as="h2" size="3xl" sx={headingSx}>
        <HStack>
          {data.icon && <Image src={data.icon} color="yellow.400" mr={1} h={7} w="auto" />}
          <Text>{data.title}</Text>
        </HStack>
      </Heading>
      <Text sx={priceSx}>{data.pricing}</Text>
      <Text sx={subtextSx}>{data.subtitle}</Text>
      {isFree && (currentTrial || (currentType && isFreeType(currentType))) ? (
        <Text color="red.800" fontSize="xl" fontWeight="bold">
          {t(`${typeNs}${currentTrial ? 'trialText' : 'freeText'}`)}
        </Text>
      ) : currentType !== type || isFreeTrialPlan(currentPlan) ? (
        <Button
          as="a"
          href={data.cta?.link}
          target="_blank"
          rel="noreferrer noopener"
          variant="outline"
          colorScheme={data.cta?.colorScheme ?? 'gray.900'}
          w="full"
        >
          {data.cta?.text}
        </Button>
      ) : null}
      {!!features?.length && (
        <Flex direction={'column'} gap={6} mt={10}>
          <Flex as="ul" sx={checkedListSx}>
            {features.map((key, i) => (
              <li key={i}>
                {checkMark}
                <div>
                  <Markdown>{key}</Markdown>
                </div>
              </li>
            ))}
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
